import Dot from "../../../../icons/Dot";
import { Button } from "../../../../shadcn/components/ui/button";
import WhatsAppIcon2 from "../../../../icons/WhatsAppIcon2";
import Heart from "../../../../icons/Heart";
import LikedIcon from "../../../../icons/LikedIcon";
import { ShareIconThick } from "../../../../icons/ShareIconThick";
import { useSelector } from "react-redux";
import { addRupeeCharToFormattedAmount, shortlistProperty } from "services";
import { KeyValueCard } from "../../../../shadcn/components/custom/KeyValueCard";
import { Card } from "../../../../shadcn/components/ui/card";
import { useMemo, useState } from "react";
import dynamic from "next/dynamic";

interface PDAuctionDetailsHeaderProps {
  propertyData: any;
  setAuthModelOpen?: (value: boolean) => void;
  setAcknowledgementOpen?: (value: boolean) => void;
  handleInterested: () => void;
  auctionDetails: any[];
  propertyDetailsFullLink: string;
  propertyType?: "auction_property" | "private_property";
}

const ShareModal = dynamic(() => import("../../common/ShareModal/ShareModal"), {
  loading: () => (
    <div className="bg-gray-200 rounded-lg w-full h-[220px] animate-pulse" />
  ),
  ssr: false,
});

export const PDAuctionDetailsHeader = ({
  propertyData,
  setAuthModelOpen = () => {},
  setAcknowledgementOpen = () => {},
  handleInterested = () => {},
  auctionDetails,
  propertyDetailsFullLink,
  propertyType = "auction_property",
}: PDAuctionDetailsHeaderProps) => {
  const {
    listing_id,
    city,
    sub_asset_type,
    uuid,
    interested_count,
    auction_by_ab,
    status,
    property_date_type,
  } = propertyData;

  const [showCopiedPopup, setShowCopiedPopup] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const userToken = useSelector((state: any) => state.auth.accessToken);

  const shortlistedProperties = useSelector(
    (state: any) => state.auth?.user?.profile?.meta?.shortlisted_properties,
  );

  const idLabel = useMemo(
    () => ({
      auction_property: `Auction ID: ${listing_id}`,
      private_property: `Listing ID: ${listing_id}`,
    }),
    [listing_id],
  );

  const isActiveAuctionConducted =
    (auction_by_ab === true ||
      auction_by_ab?.toString()?.toLowerCase() === "yes") &&
    status?.toLowerCase() === "active" &&
    property_date_type?.toLowerCase() === "dated";

  const handleShortListed = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (userToken) {
      try {
        await shortlistProperty(uuid, interested_count);
      } catch (error) {
        console.error("Error shortlisting property:", error);
      }
    } else {
      setAuthModelOpen(true);
      console.log("Open modal for login");
    }
  };

  const handleParticipateAuction = async () => {
    if (userToken) {
      if (isActiveAuctionConducted) {
        if (window != undefined) {
          const baseUrl = process.env.NEXT_PUBLIC_BIDDER_URL?.replace(
            /\/$/,
            "",
          );
          const bidderUrl = `${baseUrl}/all-events/?property_listing_id=${listing_id}&view_detail=true`;
          window.open(bidderUrl, "_blank");
        }
        return;
      } else {
        // If login user and auction is Not conducted on auction bazar portal
        // Call the Bid Interest API
        await handleInterested();
        setAcknowledgementOpen(true);
        return;
      }
    }
    setAuthModelOpen(true);
  };

  const handleWhatsappClick = () => {
    const clientNumber = "+918370969696";
    const link = propertyDetailsFullLink;
    const whatsappMsg = `Hi Auctionbazaar,\n\nI am interested in knowing more about this property\n\n${link}`;
    window.open(
      `https://wa.me/${clientNumber}?text=${encodeURI(whatsappMsg)}`,
      "_blank",
    );
  };

  return (
    <Card className="space-y-4 mt-4 py-3 bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
      <div className="lg:flex lg:justify-between space-y-2 w-full">
        <div className="space-y-2 mt-4 lg:mt-0 lg:max-w-[400px]">
          <h2 className="text-xl lg:text-2xl capitalize">
            {`${sub_asset_type?.name}`} - {`${city?.name}`}
          </h2>
          <div className="flex items-center px-3 py-1 border-[1px] border-brand-textLightGrey rounded-full w-max">
            <Dot />
            <span className="pl-2 font-normal text-[13px] text-brand-secondaryTextLabelGrey">
              {idLabel[propertyType]}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <Button
            className="bg-[#F7F7F7] hover:bg-[#F7F7F7] rounded"
            onClick={() => {
              if (
                process.env.NEXT_PUBLIC_SHARE_PROPERTY_NEW_IMPLEMENTATION ===
                "true"
              ) {
                setShareModalOpen(true);
              } else {
                setShowCopiedPopup(true);
                setTimeout(() => {
                  setShowCopiedPopup(false);
                }, 2000);
                if (document.hasFocus()) {
                  window.navigator.clipboard.writeText(window.location.href);
                }
              }
            }}
          >
            <ShareIconThick />
          </Button>
          <div
            className="bg-[#F7F7F7] px-3 py-2 rounded cursor-pointer"
            onClick={handleShortListed}
          >
            {shortlistedProperties?.includes(uuid) ? <LikedIcon /> : <Heart />}
          </div>

          <div
            className="bg-[#F2FFEA] px-3 py-2 rounded cursor-pointer"
            onClick={handleWhatsappClick}
          >
            <WhatsAppIcon2 />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 capitalize">
        {auctionDetails?.map((item, index) => {
          return (
            <KeyValueCard
              key={`auctionDetails_item_${index}_${item.label}`}
              label={item.label}
              value={item.value}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap justify-between items-center gap-2">
        {isActiveAuctionConducted && propertyData?.bidding_increment_amount ? (
          <p className="text-brand-secondaryTextLabelGrey text-sm">
            <span className="font-semibold">Minimum Bid Increment: </span>
            <span>
              {addRupeeCharToFormattedAmount(
                propertyData?.bidding_increment_amount,
              )}
            </span>
          </p>
        ) : (
          <div />
        )}
        {isActiveAuctionConducted && (
          <Button
            onClick={handleParticipateAuction}
            className="bg-brand-primaryAction hover:bg-brand-primaryActionHover px-3.5 py-2.5"
          >
            Participate in Auction
          </Button>
        )}
      </div>

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        propertyData={propertyData}
        propertyType={propertyType}
      />
      {showCopiedPopup && (
        <div
          className={
            "absolute text-xs lg:text-base z-50 bottom-1/2 mt-2 left-1/2 -translate-x-1/2 h-fit w-fit py-2 px-8 border border-white rounded-lg bg-black text-center text-white transition-all " +
            (showCopiedPopup ? "opacity-75" : "opacity-0")
          }
        >
          Link Copied to Clipboard
        </div>
      )}
    </Card>
  );
};
